import type { QueryClient } from "@tanstack/react-query";
import { CONVERSATION_QUERY_KEY } from "@/lib/query/conversation/conversation.query.key";
import type {
  MediaItem,
  TypedConversation,
  TypedMessage,
} from "@/lib/query/conversation/conversation.types";
import { MESSAGE_QUERY_KEY } from "@/lib/query/message/message.query.key";
import type { WebSocketClient } from "./client";
import type {
  ConversationDeletedDto,
  ConversationEventDto,
  ConversationUpdateEventDto,
  MessageEventDto,
  MessageReadEventDto,
} from "./types";

interface InfiniteQueryData<T> {
  pages: Array<{
    items: T[];
    count: number;
  }>;
  pageParams: number[];
}

// Helper to parse media from websocket message
// The type generation is incorrect, so we cast through unknown
function parseMessageMedia(media: unknown): MediaItem[] | undefined {
  if (!media) {
    return undefined;
  }

  // If it's already an array, map it to MediaItem format
  if (Array.isArray(media)) {
    return media.map((item: unknown) => {
      const m = item as {
        mediaUrl?: string;
        fileName?: string;
        contentType?: string;
        fileSize?: number;
      };
      return {
        mediaUrl: m.mediaUrl || "",
        fileName: m.fileName || "file",
        contentType: m.contentType || "application/octet-stream",
        fileSize: m.fileSize || 0,
      };
    });
  }

  return undefined;
}

/** Setup WebSocket event listeners that sync with TanStack Query cache */
export function setupQuerySync(client: WebSocketClient, queryClient: QueryClient): () => void {
  const handlers: Array<{ event: string; handler: (data: unknown) => void }> = [];

  // Helper to add handlers and track for cleanup
  const addHandler = <T>(event: string, handler: (data: T) => void) => {
    client.on(event as Parameters<typeof client.on>[0], handler as Parameters<typeof client.on>[1]);
    handlers.push({ event, handler: handler as (data: unknown) => void });
  };

  // Sync new messages
  addHandler<MessageEventDto>("message:new", (message) => {
    // Update messages list for the conversation
    // Messages are stored in desc order and reversed for display,
    // so we append to the FIRST page (which contains the most recent messages)
    queryClient.setQueryData<InfiniteQueryData<TypedMessage>>(
      [MESSAGE_QUERY_KEY, "infinite", message.conversationId],
      (old) => {
        if (!old?.pages?.[0]) {
          return old;
        }

        // Check if message already exists
        const exists = old.pages.some((page) => page.items.some((m) => m.id === message.id));
        if (exists) {
          return old;
        }

        // Convert MessageEventDto to TypedMessage format
        const typedMessage: TypedMessage = {
          id: message.id,
          conversationId: message.conversationId || "",
          body: message.body,
          status: message.status as TypedMessage["status"],
          direction: message.direction as TypedMessage["direction"],
          channel: message.channel as TypedMessage["channel"],
          senderId: message.senderId,
          sender: {
            id: message.sender.id,
            displayName: message.sender.displayName,
            avatarUrl: message.sender.avatarUrl ?? undefined,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isRead: false,
          media: parseMessageMedia(message.media),
        };

        return {
          ...old,
          pages: [
            {
              ...old.pages[0],
              // Prepend to first page (newest messages go first in desc order)
              items: [typedMessage, ...old.pages[0].items],
              count: old.pages[0].count + 1,
            },
            ...old.pages.slice(1),
          ],
        };
      },
    );

    // Update conversation's last message in the list
    queryClient.setQueriesData<InfiniteQueryData<TypedConversation>>(
      { queryKey: [CONVERSATION_QUERY_KEY, "infinite"] },
      (old) => {
        if (!old?.pages) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((conv) =>
              conv.id === message.conversationId
                ? {
                    ...conv,
                    lastMessagePreview: message.body,
                    lastMessageAt: new Date().toISOString(),
                  }
                : conv,
            ),
          })),
        };
      },
    );
  });

  // Sync message updates (also handles adding message if not exists - upsert behavior)
  addHandler<MessageEventDto>("message:updated", (message) => {
    queryClient.setQueryData<InfiniteQueryData<TypedMessage>>(
      [MESSAGE_QUERY_KEY, "infinite", message.conversationId],
      (old) => {
        if (!old?.pages?.[0]) {
          return old;
        }

        // Check if message exists in any page
        const messageExists = old.pages.some((page) => page.items.some((m) => m.id === message.id));

        if (messageExists) {
          // Update existing message
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((m) =>
                m.id === message.id
                  ? {
                      ...m,
                      body: message.body,
                      status: message.status as TypedMessage["status"],
                      updatedAt: new Date().toISOString(),
                      media: parseMessageMedia(message.media) ?? m.media,
                    }
                  : m,
              ),
            })),
          };
        }

        // Message doesn't exist - add it to the first page (upsert behavior)
        const typedMessage: TypedMessage = {
          id: message.id,
          conversationId: message.conversationId || "",
          body: message.body,
          status: message.status as TypedMessage["status"],
          direction: message.direction as TypedMessage["direction"],
          channel: message.channel as TypedMessage["channel"],
          senderId: message.senderId,
          sender: {
            id: message.sender.id,
            displayName: message.sender.displayName,
            avatarUrl: message.sender.avatarUrl ?? undefined,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isRead: false,
          media: parseMessageMedia(message.media),
        };

        return {
          ...old,
          pages: [
            {
              ...old.pages[0],
              items: [typedMessage, ...old.pages[0].items],
              count: old.pages[0].count + 1,
            },
            ...old.pages.slice(1),
          ],
        };
      },
    );
  });

  // Sync message read status
  addHandler<MessageReadEventDto>("message:read", (data) => {
    queryClient.setQueryData<InfiniteQueryData<TypedMessage>>(
      [MESSAGE_QUERY_KEY, "infinite", data.conversationId],
      (old) => {
        if (!old?.pages) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((m) =>
              m.id === data.messageId
                ? {
                    ...m,
                    status: "READ" as const,
                    isRead: true,
                  }
                : m,
            ),
          })),
        };
      },
    );
  });

  // Sync new conversations - invalidate to refetch with full data
  addHandler<ConversationEventDto>("conversation:new", () => {
    // Invalidate conversations query to trigger a refetch with complete data
    // This ensures we get all fields including participants, windows, etc.
    queryClient.invalidateQueries({ queryKey: [CONVERSATION_QUERY_KEY, "infinite"] });
  });

  // Sync conversation updates
  addHandler<ConversationUpdateEventDto>("conversation:updated", (data) => {
    queryClient.setQueriesData<InfiniteQueryData<TypedConversation>>(
      { queryKey: [CONVERSATION_QUERY_KEY, "infinite"] },
      (old) => {
        if (!old?.pages) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.map((conv) =>
              conv.id === data.conversationId
                ? {
                    ...conv,
                    ...(data.title && { title: data.title, displayTitle: data.title }),
                    ...(data.lastMessagePreview && { lastMessagePreview: data.lastMessagePreview }),
                    ...(data.lastMessageAt && { lastMessageAt: data.lastMessageAt }),
                    ...(data.suggestedMessage && { suggestedMessage: data.suggestedMessage }),
                    ...(data.unreadCount !== undefined && {
                      unreadCount: parseInt(data.unreadCount, 10) || 0,
                    }),
                  }
                : conv,
            ),
          })),
        };
      },
    );
  });

  // Sync conversation deletion
  addHandler<ConversationDeletedDto>("conversation:deleted", (data) => {
    queryClient.setQueriesData<InfiniteQueryData<TypedConversation>>(
      { queryKey: [CONVERSATION_QUERY_KEY, "infinite"] },
      (old) => {
        if (!old?.pages) {
          return old;
        }

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((conv) => conv.id !== data.conversationId),
            count: Math.max(0, page.count - 1),
          })),
        };
      },
    );

    // Remove cached messages for this conversation
    queryClient.removeQueries({
      queryKey: [MESSAGE_QUERY_KEY, "infinite", data.conversationId],
    });
  });

  // Return cleanup function
  return () => {
    handlers.forEach(({ event, handler }) => {
      client.off(
        event as Parameters<typeof client.off>[0],
        handler as Parameters<typeof client.off>[1],
      );
    });
  };
}
